import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import { useDispatch, useSelector } from "react-redux";
import { SAGA_ACTIONS } from "../../_config";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppUserDataSelector } from "src/_common/hooks/selectors/userSelector";
import { useHistory } from "react-router-dom";

interface VideoCVFiltersProps {
  onApplyFilters: (filters: {
    keywords: string;
    location: string;
    country_id: string;
    city_id: string;
    skills: string;
  }) => void;
  onResetFilters: () => void;
  context?: "videoCv" | "candidate";
}

const VideoCVFilters: React.FC<VideoCVFiltersProps> = ({ onApplyFilters, onResetFilters, context = "videoCv" }) => {
  const { t } = useTranslation("video-cv");
  const dispatch = useDispatch();
  const toast = useToaster();
  const history = useHistory();
  const userData = useAppUserDataSelector();

  // Check if user is on free plan
  const isFreePlan = userData?.subscription?.price === 0;

  // Local state for filters
  const [filters, setFilters] = useState({
    keywords: "",
    location: "",
    experienceLevel: [] as string[],
    skills: "",
  });

  // Country autocomplete states
  const [countrySelectKey, setCountrySelectKey] = useState(0);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [selectedCountryName, setSelectedCountryName] = useState<string>("");
  const [hasCountryInteracted, setHasCountryInteracted] = useState(false);
  const [countries, setCountries] = useState<Array<{ id: string; name: string }>>([]);

  // City autocomplete states
  const [citySelectKey, setCitySelectKey] = useState(0);
  const [selectedCityId, setSelectedCityId] = useState<string>("");
  const [selectedCityName, setSelectedCityName] = useState<string>("");
  const [hasCityInteracted, setHasCityInteracted] = useState(false);
  const [cities, setCities] = useState<Array<{ id: string; name: string }>>([]);
  const isCityDisabled = !selectedCountryId;

  // Skills autocomplete states
  const [skillsSelectKey, setSkillsSelectKey] = useState(0);
  const [selectedSkillId, setSelectedSkillId] = useState<string>("");
  const [selectedSkillName, setSelectedSkillName] = useState<string>("");
  const [skillInputText, setSkillInputText] = useState<string>("");
  const [hasSkillInteracted, setHasSkillInteracted] = useState(false);
  const [skillsList, setSkillsList] = useState<Array<{ id: string; name: string }>>([]);

  // Redux selectors
  const { countriesNew } = useSelector((state: any) => ({
    countriesNew: state.global.countriesNew,
  }));

  const { citiesNew } = useSelector((state: any) => ({
    citiesNew: state.global.citiesNew,
  }));

  const { skills } = useSelector((state: any) => ({
    skills: state.global.skills,
  }));

  // Format countries from Redux when data changes
  useEffect(() => {
    if (countriesNew && countriesNew.length > 0) {
      const formattedCountries = countriesNew.map((country: any) => ({
        id: country.id.toString(),
        name: country.name,
      }));
      setCountries(formattedCountries);
      if (countryCurrentSearchRef.current) { setCountryInputSearch(countryCurrentSearchRef.current); }
    }
  }, [countriesNew]);

  // Format cities from Redux when data changes
  useEffect(() => {
    if (citiesNew && citiesNew.length > 0) {
      const formattedCities = citiesNew.map((city: any) => ({
        id: city.id.toString(),
        name: city.name,
      }));
      setCities(formattedCities);
      if (cityCurrentSearchRef.current) { setCityInputSearch(cityCurrentSearchRef.current); }
    }
  }, [citiesNew]);

  // Format skills from Redux when data changes
  useEffect(() => {
    if (skills && skills.length > 0) {
      const formattedSkills = skills.map((skill: any) => ({
        id: skill.id.toString(),
        name: skill.name,
      }));
      setSkillsList(formattedSkills);
      if (skillsCurrentSearchRef.current) { setSkillsInputSearch(skillsCurrentSearchRef.current); }
    }
  }, [skills]);

  // Fetch initial skills
  useEffect(() => {
    dispatch({
      type: SAGA_ACTIONS.COMMON.SKILLS,
      payload: { limit: 30, offset: 0 },
    });
  }, [dispatch]);

  const countryDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const cityDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const skillsDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const countryCurrentSearchRef = useRef<string>("");
  const [countryInputSearch, setCountryInputSearch] = useState<string>("");
  const cityCurrentSearchRef = useRef<string>("");
  const [cityInputSearch, setCityInputSearch] = useState<string>("");
  const skillsCurrentSearchRef = useRef<string>("");
  const [skillsInputSearch, setSkillsInputSearch] = useState<string>("");

  // Fetch countries from API
  const getCountries = (searchName: string = "") => {
    const params: any = { limit: 30 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: params,
    });
  };

  // Fetch cities from API
  const getCities = (countryId: string, searchName: string = "") => {
    const params: any = { limit: 30, country_id: countryId };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.CITIES_NEW,
      payload: params,
    });
  };

  // Handle country selection
  const handleCountrySelect = (item: any) => {
    setCountryInputSearch(item.name);
    setSelectedCountryId(item.id);
    setSelectedCountryName(item.name);
    handleFilterChange("location", item.name);
    // Reset city when country changes
    setSelectedCityId("");
    setSelectedCityName("");
    setHasCityInteracted(false);
    setCitySelectKey((k) => k + 1);
    // Load cities for selected country
    getCities(item.id);
    // Reset interaction and remount to close dropdown
    setHasCountryInteracted(false);
    setCountrySelectKey((k) => k + 1);
  };

  // Handle city selection
  const handleCitySelect = (item: any) => {
    setCityInputSearch(item.name);
    setSelectedCityId(item.id);
    setSelectedCityName(item.name);
    // Reset interaction and remount to close dropdown
    setHasCityInteracted(false);
    setCitySelectKey((k) => k + 1);
  };

  // Fetch skills from API
  const getSkills = (searchName: string = "") => {
    const params: any = { limit: 30, offset: 0 };
    if (searchName.trim()) {
      params.q = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.SKILLS,
      payload: params,
    });
  };

  // Handle skill selection
  const handleSkillSelect = (item: any) => {
    setSkillsInputSearch(item.name);
    setSelectedSkillId(item.id);
    setSelectedSkillName(item.name);
    setSkillInputText(item.name);
    handleFilterChange("skills", item.name);
    // Reset interaction and remount to close dropdown
    setHasSkillInteracted(false);
    setSkillsSelectKey((k) => k + 1);
  };

  const handleFilterChange = (filterType: string, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleApplyFilters = () => {
    // Check if country is selected (mandatory)
    if (!selectedCountryId) {
      toast.warning(t("videoCv.filters.countryRequired"));
      return;
    }

    // Check if either keyword or skill is entered
    const hasKeyword = filters.keywords.trim() !== "";
    const hasSkill = skillInputText.trim() !== "";

    if (!hasKeyword && !hasSkill) {
      toast.warning(t("videoCv.filters.keywordOrSkillRequired"));
      return;
    }

    // Use skillInputText (the typed value) instead of selectedSkillName
    onApplyFilters({
      keywords: filters.keywords,
      location: filters.location,
      country_id: selectedCountryId,
      city_id: selectedCityId,
      skills: skillInputText.trim(),
    });
  };

  const handleResetFilters = () => {
    // Check if any filters are currently set
    const hasActiveFilters =
      filters.keywords.trim() !== "" ||
      filters.location.trim() !== "" ||
      filters.skills.trim() !== "" ||
      selectedCountryId !== "" ||
      skillInputText.trim() !== "";

    // Reset all filter states
    setFilters({
      keywords: "",
      location: "",
      experienceLevel: [],
      skills: "",
    });
    setSelectedCountryId("");
    setSelectedCountryName("");
    setHasCountryInteracted(false);
    setCountrySelectKey((k) => k + 1);
    setSelectedCityId("");
    setSelectedCityName("");
    setHasCityInteracted(false);
    setCitySelectKey((k) => k + 1);
    setSelectedSkillId("");
    setSelectedSkillName("");
    setSkillInputText("");
    setHasSkillInteracted(false);
    setSkillsSelectKey((k) => k + 1);

    // Only notify parent component (trigger API call) if filters were actually set
    if (hasActiveFilters) {
      onResetFilters();
    }
  };

  const hasActiveFilters = filters.keywords.trim() !== "" || selectedCountryId !== "" || skillInputText.trim() !== "";

  return (
    <div className="video-cv-filters-outer">
      <div className="video-cv-filters">
        <div className="filters-header">
          <h3>{t("videoCv.filters.title", "Filters")}</h3>
          <button className={`reset-button ${!hasActiveFilters ? "d-none" : ""}`} onClick={handleResetFilters}>
            {t("videoCv.filters.reset", "Reset")}
          </button>
        </div>

        <div className="filter-group">
          <label>{t("videoCv.filters.keywords", "Keywords")}</label>
          <div className="input-with-icon">
            <i className="search-icon fa fa-search"></i>
            <input
              type="text"
              placeholder={
                context === "videoCv"
                  ? t("videoCv.filters.keywordsPlaceholder")
                  : t("videoCv.filters.candidatePlaceholder")
              }
              value={filters.keywords}
              onChange={(e) => handleFilterChange("keywords", e.target.value)}
            />
          </div>
        </div>

        <div className="filter-group position-relative z-3">
          <label>
            {t("videoCv.filters.location", "Country")} <span className="text-danger">*</span>
          </label>
          <div className="autocomplete-container">
            <ReactSearchAutocomplete
              key={countrySelectKey}
              items={hasCountryInteracted ? countries : []}
              onSearch={(string, results) => {
                countryCurrentSearchRef.current = string;
                setHasCountryInteracted(true);
                if (countryDebounceRef.current) clearTimeout(countryDebounceRef.current);
                countryDebounceRef.current = setTimeout(() => {
                  if (string.length > 0) {
                    getCountries(string);
                  } else {
                    getCountries();
                  }
                }, 300);
              }}
              onFocus={() => {
                if (!hasCountryInteracted) {
                  setHasCountryInteracted(true);
                  getCountries();
                }
              }}
              onSelect={handleCountrySelect}
              onClear={() => {
                countryCurrentSearchRef.current = "";
                setCountryInputSearch("");
                setSelectedCountryId("");
                setSelectedCountryName("");
                handleFilterChange("location", "");
                setHasCountryInteracted(false);
                setCountrySelectKey((k) => k + 1);
                // Reset city when country is cleared
                setSelectedCityId("");
                setSelectedCityName("");
                setHasCityInteracted(false);
                setCitySelectKey((k) => k + 1);
                getCountries();
              }}
              placeholder={t("videoCv.filters.locationPlaceholder", "Select country")}
              inputSearchString={countryInputSearch}
              showIcon={false}
              showNoResults={false}
              autoFocus={false}
              styling={{
                height: "45px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "white",
                boxShadow: "none",
                hoverBackgroundColor: "#f3f4f6",
                color: "#374151",
                fontSize: "14px",
                fontFamily: "inherit",
                iconColor: "#6b7280",
                lineColor: "#e5e7eb",
                placeholderColor: "#9ca3af",
                clearIconMargin: "3px 8px 0 0",
                zIndex: 2,
              }}
            />
          </div>
        </div>

        <div className={`filter-group position-relative z-2${isCityDisabled ? " opacity-50" : ""}`}>
          <label>{t("videoCv.filters.city", "City")}</label>
          <div className="autocomplete-container">
            <ReactSearchAutocomplete
              key={citySelectKey}
              items={hasCityInteracted ? cities : []}
              onSearch={(string, results) => {
                if (isCityDisabled) return;
                cityCurrentSearchRef.current = string;
                setHasCityInteracted(true);
                if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
                cityDebounceRef.current = setTimeout(() => {
                  getCities(selectedCountryId, string.length > 0 ? string : "");
                }, 300);
              }}
              onFocus={() => {
                if (isCityDisabled) return;
                if (!hasCityInteracted) {
                  setHasCityInteracted(true);
                  getCities(selectedCountryId);
                }
              }}
              onSelect={handleCitySelect}
              onClear={() => {
                cityCurrentSearchRef.current = "";
                setCityInputSearch("");
                setSelectedCityId("");
                setSelectedCityName("");
                setHasCityInteracted(false);
                setCitySelectKey((k) => k + 1);
              }}
              placeholder={t("videoCv.filters.cityPlaceholder")}
              inputSearchString={cityInputSearch}
              showIcon={false}
              showNoResults={false}
              autoFocus={false}
              styling={{
                height: "45px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: isCityDisabled ? "#f9fafb" : "white",
                boxShadow: "none",
                hoverBackgroundColor: "#f3f4f6",
                color: "#374151",
                fontSize: "14px",
                fontFamily: "inherit",
                iconColor: "#6b7280",
                lineColor: "#e5e7eb",
                placeholderColor: "#9ca3af",
                clearIconMargin: "3px 8px 0 0",
                zIndex: 2,
              }}
            />
          </div>
        </div>

        <div className="filter-group position-relative z-0">
          <label>{t("videoCv.filters.skills", "Skills")}</label>
          <div className="autocomplete-container">
            <ReactSearchAutocomplete
              key={skillsSelectKey}
              items={hasSkillInteracted ? skillsList : []}
              onSearch={(string, results) => {
                skillsCurrentSearchRef.current = string;
                setSkillInputText(string);
                setHasSkillInteracted(true);
                if (skillsDebounceRef.current) clearTimeout(skillsDebounceRef.current);
                skillsDebounceRef.current = setTimeout(() => {
                  if (string.length > 0) {
                    getSkills(string);
                  } else {
                    getSkills();
                  }
                }, 300);
              }}
              onFocus={() => {
                if (!hasSkillInteracted) {
                  setHasSkillInteracted(true);
                  getSkills();
                }
              }}
              onSelect={handleSkillSelect}
              onClear={() => {
                skillsCurrentSearchRef.current = "";
                setSkillsInputSearch("");
                setSelectedSkillId("");
                setSelectedSkillName("");
                setSkillInputText("");
                handleFilterChange("skills", "");
                setHasSkillInteracted(false);
                setSkillsSelectKey((k) => k + 1);
                getSkills();
              }}
              placeholder={t("videoCv.filters.skillsPlaceholder", "e.g., React, python")}
              inputSearchString={skillsInputSearch}
              showIcon={false}
              showNoResults={false}
              autoFocus={false}
              styling={{
                height: "45px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "white",
                boxShadow: "none",
                hoverBackgroundColor: "#f3f4f6",
                color: "#374151",
                fontSize: "14px",
                fontFamily: "inherit",
                iconColor: "#6b7280",
                lineColor: "#e5e7eb",
                placeholderColor: "#9ca3af",
                clearIconMargin: "3px 8px 0 0",
                zIndex: 2,
              }}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn-cancel d-none" onClick={handleResetFilters}>
            {t("videoCv.filters.cancel", "Cancel")}
          </button>
          <button className="btn-preview" onClick={handleApplyFilters}>
            {t("videoCv.filters.applyFilter", "Apply Filter")}
          </button>
        </div>
      </div>

      {/* Free Plan Upgrade Notice */}
      {isFreePlan && (
        <div className="free-plan-notice mt-3 p-3 bg-light border rounded text-center">
          <div className="mb-2">
            <i
              className={`fa ${context === "videoCv" ? "fa-video" : "fa-search"} text-muted mb-2`}
              style={{ fontSize: "20px" }}
            ></i>
            <p className="mb-1 fw-semibold" style={{ fontSize: "14px" }}>
              {context === "videoCv"
                ? t("videoCv.filters.freePlanTitleVideoCv", "Only 10 Video CVs are available on the Free plan")
                : t("videoCv.filters.freePlanTitleCandidate", "You're viewing 10 candidates on the Free plan")}
            </p>
            <p className="text-muted mb-3" style={{ fontSize: "12px" }}>
              {t("videoCv.filters.freePlanSubtitle", "Upgrade to view more candidates.")}
            </p>
          </div>
          <button className="btn-preview" onClick={() => history.push("/settings/subscriptions")}>
            {t("videoCv.filters.upgradePlan")}
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoCVFilters;
